import { familiesInBundle, listBundleVersions, taskIdsInFamily } from "@talent/tasks";
import { taskLabel } from "../copy.js";

/**
 * Which games the public page advertises.
 *
 * Extracted from `landing.tsx` and pure, for the reason `back-target.ts` and `route.ts` are: the part
 * with a decision in it should be testable without a DOM. This one earned it the hard way — the
 * bundle version was a string literal in the component, it outlived the bundle it named, and
 * `getBundle` threw during render. The whole landing page went blank, which is the one screen where
 * that failure is invisible to everybody who is already signed in.
 */

/**
 * The newest shipped bundle.
 *
 * **The only place in the app allowed to ask for "the newest"**, and it is safe here for exactly the
 * reason it is forbidden everywhere else: this page describes the product to a stranger, and no
 * child's record is being read against it. A client that chose its own bundle for a PLAYING child
 * could move them across a content change mid-year with no trace in the data (ADR-002) — which is why
 * `repositories/bundles.ts` has no such function and `getBundle("latest")` throws.
 *
 * `listBundleVersions()` returns them in ship order, newest last.
 */
export function latestBundleVersion(): string {
  const versions = listBundleVersions();
  const newest = versions[versions.length - 1];
  // A build with no content at all. Throwing names the cause; returning "" would surface as an empty
  // shelf on the marketing page and look like a styling bug.
  if (!newest) throw new Error("no content bundle ships at all");
  return newest;
}

export interface ShelfEntry {
  family: string;
  label: string;
}

/**
 * One entry per FAMILY, never per task.
 *
 * The two A-13 arms of forward span are the same game with a different wrapper. Listing both would
 * show a visitor two games that are one — and, worse, it would put both arms of a live experiment on a
 * public page, which is how an experiment stops measuring anything.
 */
export function shelfEntries(bundleVersion = latestBundleVersion()): ShelfEntry[] {
  return familiesInBundle(bundleVersion).flatMap((family) => {
    const taskId = taskIdsInFamily(bundleVersion, family)[0];
    return taskId ? [{ family, label: taskLabel(taskId) }] : [];
  });
}

/**
 * One family per broad category, picked for how it looks on a card rather than for what it
 * teaches — this is the set a stranger sees before deciding whether the rest is worth a look.
 *
 * A curated subset rather than the full shelf: a grid of every family repeats the same handful of
 * accent colours several times over and reads as a spec sheet, which is the opposite of what a
 * landing page's shelf is for (`GameShelf` in `landing.tsx`). The real count still goes on the page —
 * `gamesCount` is filled from `shelfEntries().length`, never from this list's length — so trimming
 * the grid can never make the page understate what actually ships.
 *
 * Listed by family id rather than by position, so a bundle change that drops one of these just drops
 * it from the result instead of shifting the rest — see the filter below.
 */
const FEATURED_FAMILIES = [
  "math-arith", // bee — counting and arithmetic
  "lit-find-letter", // squirrel — letters
  "vs-sequence-color", // ladybird — the most colourful of the pattern games
  "logic-sudoku", // frog — the shape grid
  "vs-robot", // robot — the one every child asks about by name
  "vs-mirror", // butterfly — symmetry has no better mascot
  "wm-pairs", // penguin — memory
  "wm-span-forward", // firefly — the app's own mascot creature
] as const;

/**
 * The curated set, in `FEATURED_FAMILIES` order and never longer than it — a family the current
 * bundle does not ship is simply absent rather than replaced, so this can only shrink, never lie.
 */
export function featuredShelfEntries(bundleVersion = latestBundleVersion()): ShelfEntry[] {
  const byFamily = new Map(shelfEntries(bundleVersion).map((entry) => [entry.family, entry]));
  return FEATURED_FAMILIES.flatMap((family) => byFamily.get(family) ?? []);
}
